// ─── Demo Query Builder ───────────────────────────────────────────────────────
// Bildet die Supabase Query-Builder-API nach, gibt statische Demo-Daten zurück.

type FilterFn = (row: any) => boolean;

export function createDemoQueryBuilder(tableName: string, demoData: Record<string, any[]>) {
  let data = [...(demoData[tableName] || [])];
  let countMode = false;
  let headMode = false;

  const applyFilter = (fn: FilterFn) => { data = data.filter(fn); };

  const builder: any = {
    // ── Lese-Methoden (chainable) ──────────────────────────────────────

    select(cols?: string, opts?: { count?: string; head?: boolean }) {
      if (opts?.count === 'exact') countMode = true;
      if (opts?.head) headMode = true;
      return builder;
    },

    eq(col: string, val: any) {
      applyFilter(r => r[col] === val);
      return builder;
    },

    neq(col: string, val: any) {
      applyFilter(r => r[col] !== val);
      return builder;
    },

    in(col: string, vals: any[]) {
      applyFilter(r => vals.includes(r[col]));
      return builder;
    },

    ilike(col: string, pattern: string) {
      const cleaned = pattern.replace(/%/g, '.*');
      const regex = new RegExp(cleaned, 'i');
      applyFilter(r => regex.test(String(r[col] ?? '')));
      return builder;
    },

    or(expression: string) {
      // Parse Supabase OR-Syntax: "col1.op.val,col2.op.val"
      try {
        const parts = parseOrExpression(expression);
        applyFilter(r => parts.some(part => evaluateCondition(r, part)));
      } catch {
        // Fallback: don't filter
      }
      return builder;
    },

    gte(col: string, val: any) {
      applyFilter(r => r[col] >= val);
      return builder;
    },

    lte(col: string, val: any) {
      applyFilter(r => r[col] <= val);
      return builder;
    },

    gt(col: string, val: any) {
      applyFilter(r => r[col] > val);
      return builder;
    },

    lt(col: string, val: any) {
      applyFilter(r => r[col] < val);
      return builder;
    },

    not(col: string, op: string, val: any) {
      if (op === 'is') {
        applyFilter(r => r[col] !== val && r[col] !== null && r[col] !== undefined);
      } else if (op === 'eq') {
        applyFilter(r => r[col] !== val);
      } else if (op === 'in') {
        // .not('col', 'in', '(a,b)')
        const parsed = String(val).replace(/[()]/g, '').split(',');
        applyFilter(r => !parsed.includes(String(r[col])));
      }
      return builder;
    },

    is(col: string, val: any) {
      if (val === null) {
        applyFilter(r => r[col] === null || r[col] === undefined);
      } else {
        applyFilter(r => r[col] === val);
      }
      return builder;
    },

    filter(col: string, op: string, val: any) {
      // Generic filter method
      switch (op) {
        case 'eq': return builder.eq(col, val);
        case 'neq': return builder.neq(col, val);
        case 'gt': return builder.gt(col, val);
        case 'gte': return builder.gte(col, val);
        case 'lt': return builder.lt(col, val);
        case 'lte': return builder.lte(col, val);
        case 'ilike': return builder.ilike(col, val);
        case 'is': return builder.is(col, val);
        default: return builder;
      }
    },

    order(col: string, opts?: { ascending?: boolean; referencedTable?: string }) {
      // Sortierung eingebetteter Tabellen kennt der Demo-Builder nicht – Hauptliste nicht verändern
      if (opts?.referencedTable) return builder;
      const asc = opts?.ascending !== false;
      data.sort((a, b) => {
        const aVal = a[col] ?? '';
        const bVal = b[col] ?? '';
        if (aVal < bVal) return asc ? -1 : 1;
        if (aVal > bVal) return asc ? 1 : -1;
        return 0;
      });
      return builder;
    },

    limit(n: number, opts?: { referencedTable?: string }) {
      if (opts?.referencedTable) return builder;
      data = data.slice(0, n);
      return builder;
    },

    range(from: number, to: number) {
      data = data.slice(from, to + 1);
      return builder;
    },

    // ── Terminal-Methoden ───────────────────────────────────────────────

    single() {
      return Promise.resolve({ data: data[0] || null, error: null });
    },

    maybeSingle() {
      return Promise.resolve({ data: data[0] || null, error: null });
    },

    // Thenable — ermöglicht `await supabase.from('x').select()`
    then(resolve: (result: any) => void, reject?: (err: any) => void) {
      try {
        if (headMode && countMode) {
          resolve({ data: null, error: null, count: data.length });
        } else if (countMode) {
          resolve({ data, error: null, count: data.length });
        } else {
          resolve({ data, error: null });
        }
      } catch (e) {
        if (reject) reject(e);
      }
    },
  };

  return builder;
}

// ─── Schreib-Builder (simuliert Erfolg) ───────────────────────────────────────

export function createDemoWriteBuilder(_data?: any) {
  const builder: any = {
    select() { return builder; },
    eq() { return builder; },
    neq() { return builder; },
    in() { return builder; },
    is() { return builder; },
    not() { return builder; },
    or() { return builder; },
    ilike() { return builder; },
    gte() { return builder; },
    lte() { return builder; },
    gt() { return builder; },
    lt() { return builder; },
    filter() { return builder; },
    order() { return builder; },
    limit() { return builder; },
    range() { return builder; },
    single() {
      return Promise.resolve({ data: _data ? { id: crypto.randomUUID(), ...(Array.isArray(_data) ? _data[0] : _data) } : { id: crypto.randomUUID() }, error: null });
    },
    maybeSingle() {
      return Promise.resolve({ data: null, error: null });
    },
    then(resolve: (result: any) => void) {
      resolve({ data: _data || null, error: null });
    },
  };
  return builder;
}

// ─── OR-Expression Parser ─────────────────────────────────────────────────────

interface ParsedCondition {
  col: string;
  op: string;
  val: string;
}

function parseOrExpression(expr: string): ParsedCondition[] {
  // Format: "col1.ilike.%val%,col2.ilike.%val%"
  // Splits on commas that are NOT inside parentheses
  const parts: ParsedCondition[] = [];
  const segments = expr.split(',');

  for (const segment of segments) {
    const trimmed = segment.trim();
    // Match: column.operator.value
    const dotIdx1 = trimmed.indexOf('.');
    if (dotIdx1 === -1) continue;
    const col = trimmed.substring(0, dotIdx1);
    const rest = trimmed.substring(dotIdx1 + 1);
    const dotIdx2 = rest.indexOf('.');
    if (dotIdx2 === -1) continue;
    const op = rest.substring(0, dotIdx2);
    const val = rest.substring(dotIdx2 + 1);
    parts.push({ col, op, val });
  }

  return parts;
}

function evaluateCondition(row: any, cond: ParsedCondition): boolean {
  const rowVal = row[cond.col];
  switch (cond.op) {
    case 'eq':
      return String(rowVal) === cond.val;
    case 'neq':
      return String(rowVal) !== cond.val;
    case 'ilike': {
      const regex = new RegExp(cond.val.replace(/%/g, '.*'), 'i');
      return regex.test(String(rowVal ?? ''));
    }
    case 'like': {
      const regex = new RegExp(cond.val.replace(/%/g, '.*'));
      return regex.test(String(rowVal ?? ''));
    }
    case 'gt':
      return rowVal > cond.val;
    case 'gte':
      return rowVal >= cond.val;
    case 'lt':
      return rowVal < cond.val;
    case 'lte':
      return rowVal <= cond.val;
    case 'is':
      if (cond.val === 'null') return rowVal === null || rowVal === undefined;
      return rowVal === cond.val;
    default:
      return true;
  }
}
